/**
 * Troop cards — the same visual language as the hero portraits, at a fifth of
 * the size and sixty times over.
 *
 * ── What a troop card has to do ─────────────────────────────────────────────
 *
 * The company bar gives a card 94 pixels of width and 52 of art, at the bottom
 * of the screen, during a siege. Nobody reads it. It is *glanced* at, once,
 * while something is on fire. So the card is not a small illustration: it is a
 * three-part signal, and every part has to survive being 88 pixels wide.
 *
 *   the head       says what people these are — orc, dwarf, elf, skeleton
 *   the weapon     says what job they do — spear, bow, hammer, staff
 *   the colour     says whose they are — one saturated accent, per faction
 *
 * Everything else on the card is atmosphere. That ordering is deliberate and it
 * is why the head is drawn at sixty per cent of the frame height rather than at
 * a realistic proportion: a correctly-proportioned bust at this size is a beige
 * dot on a dark rectangle, which is what the old eight role silhouettes were
 * trying not to be and, being eight shapes for sixty units, could not avoid.
 *
 * ── Why it is built from `vis` and not authored per unit ────────────────────
 *
 * There are sixty-odd unit types and there will be more. Authoring a card each
 * is how you get sixty cards of wildly uneven quality and a rule that adding a
 * unit needs an artist. Instead a card is composed:
 *
 *   kin      the body plan, from the unit's faction — an Ironfang orc and an
 *            Ashen militiaman are not the same body in two colours, which was
 *            the standing complaint about the old cards and is the whole reason
 *            this file exists
 *   build    the framing, from `vis.build` — a swarm is three small heads, a
 *            siege engine is not a person at all, a brute fills the frame
 *   kit      helm, pauldrons, shield, cloak, banner, from `vis`
 *   weapon   from `vis.weapon`, drawn large and to one side
 *
 * So a new unit in `units.ts` arrives with a card that is already right, and the
 * only art decision left is which faction it belongs to — which is a data
 * decision it had to make anyway.
 *
 * ── The rules it is under ───────────────────────────────────────────────────
 *
 * The world is ash and bone. Cloth, metal, skin and ground are all drawn from
 * that family; the faction accent appears on a pennant, a shield boss, a staff
 * orb, one trim line and the backlight, and nowhere else. A card that glows
 * looks wrong next to the map, and the map is what it sits on.
 *
 * Deterministic, always. The one place variation would help — which way a lock
 * of hair falls — is keyed off a hash of the unit id rather than a roll, so the
 * same unit draws the same way in every frame of every run.
 */

import { C, mix, shade } from '../data/palette.ts';
import { faction, type FactionDef } from '../data/factions.ts';
import { unitTypeById, type UnitType, type VisWeapon } from '../data/units.ts';
import { UF } from '../sim/types.ts';

const hex = (n: number): string => `#${(n >>> 0).toString(16).padStart(6, '0')}`;

/**
 * A stable number from a unit id.
 *
 * Not a random source — a *hash*. It exists so two units of the same kin can
 * carry their hair at slightly different angles without either of them ever
 * changing between frames, saves or machines. `Math.random` is banned in the
 * simulation and the same discipline applies to anything the player will see
 * twice.
 */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

/* ── The drawing frame ───────────────────────────────────────────────────── */

/**
 * 68 × 40, because the HUD's portrait slot is 88 × 52 and 68/40 lands within a
 * percent of 88/52. The art fits the slot without cropping and without
 * letterboxing, at the one size that matters most.
 */
const W = 68;
const H = 40;
/** Centre line. The head sits on it; the weapon goes right, the shield left. */
const CX = 34;

/* ── Kin: the body plans ─────────────────────────────────────────────────── */

interface Kin {
  skin: number;
  skinShade: number;
  /** Eyes: colour, and whether they glow in the dark of a helm. */
  eye: number;
  glow: boolean;
  /** Half-width at the temple and at the jaw. */
  headW: number;
  jawW: number;
  /** Top of the skull, and the chin. */
  top: number;
  chin: number;
  /** Eye line. */
  eyeY: number;
  eyeDx: number;
  eyeR: number;
  /** How far the face pushes forward past the skull. 0 is a flat human face. */
  muzzle: number;
  ear: 'human' | 'long' | 'beast' | 'round' | 'fin' | 'none';
  /** Half-width at the shoulder, and where the shoulder line sits. */
  shoulder: number;
  shoulderY: number;
  neck: number;
  /** Hair, beard, tusks, bandages — whatever this people wears on its face. */
  extra?: (k: Kin, f: FactionDef | undefined, seed: number) => string;
}

const HUMAN: Kin = {
  skin: 0xcaa98a,
  skinShade: 0x9b7c60,
  eye: 0x2a211b,
  glow: false,
  headW: 8.4,
  jawW: 6.2,
  top: 3,
  chin: 23.5,
  eyeY: 14.6,
  eyeDx: 3.3,
  eyeR: 1.15,
  muzzle: 0,
  ear: 'human',
  shoulder: 24,
  shoulderY: 25,
  neck: 4.2,
};

/**
 * Nine peoples, and the differences are structural rather than palette-deep.
 *
 * The test each of these has to pass is the one in the brief: with the labels
 * off, at 88 pixels, an Ashen spearman and an Ironfang grunt must not be the
 * same body in two colours. So the orc is wider at the jaw than at the temple
 * and has tusks that break his silhouette upward; the dwarf's beard is a third
 * of his card; the rat's head is mostly snout and ears; the skeleton has no
 * skin at all. None of those is a tint.
 */
const KIN: Record<string, Kin> = {
  human: HUMAN,

  /** Karrun-Dûr. Wide, low, and two-thirds beard — the beard *is* the silhouette. */
  dwarf: {
    ...HUMAN,
    skin: 0xc39c78,
    skinShade: 0x916f52,
    headW: 9.2,
    jawW: 8.0,
    top: 5.5,
    chin: 22,
    eyeY: 14.2,
    eyeDx: 3.6,
    eyeR: 1.05,
    ear: 'none',
    shoulder: 26,
    shoulderY: 26,
    neck: 5.4,
    extra: (k) =>
      // Down past the shoulder line and off the bottom of the card.
      `<path d="M${CX - k.jawW - 1.6} ${k.eyeY + 2} C${CX - k.jawW - 3.4} ${k.chin + 6} ${CX - 7} ${H} ${CX} ${H} ` +
      `C${CX + 7} ${H} ${CX + k.jawW + 3.4} ${k.chin + 6} ${CX + k.jawW + 1.6} ${k.eyeY + 2} ` +
      `C${CX + k.jawW} ${k.eyeY + 8} ${CX + 3} ${k.eyeY + 5.4} ${CX} ${k.eyeY + 5.6} ` +
      `C${CX - 3} ${k.eyeY + 5.4} ${CX - k.jawW} ${k.eyeY + 8} ${CX - k.jawW - 1.6} ${k.eyeY + 2} Z" ` +
      `fill="${hex(0x6b5137)}"/>` +
      `<path d="M${CX} ${k.eyeY + 5.6} C${CX + 3} ${k.eyeY + 5.4} ${CX + k.jawW} ${k.eyeY + 8} ${CX + k.jawW + 1.6} ${k.eyeY + 2} ` +
      `C${CX + k.jawW + 3.4} ${k.chin + 6} ${CX + 7} ${H} ${CX} ${H} ` +
      `C${CX + 2.4} ${k.chin + 8} ${CX + 2} ${k.eyeY + 12} ${CX + 1} ${k.eyeY + 6} Z" ` +
      `fill="${hex(0x836847)}" opacity="0.7"/>` +
      // Moustache over the mouth, and a nose big enough to sit above it.
      `<ellipse cx="${CX}" cy="${k.eyeY + 4}" rx="2.2" ry="1.9" fill="${hex(k.skinShade)}"/>`,
  },

  /** The Verdant Choir. Narrow, high-cheeked, and the ears do the identifying. */
  elf: {
    ...HUMAN,
    skin: 0xd6c3a6,
    skinShade: 0xa89272,
    eye: 0x53703a,
    headW: 7.4,
    jawW: 4.4,
    top: 2.5,
    chin: 24,
    eyeY: 14,
    eyeDx: 3.2,
    eyeR: 1.25,
    ear: 'long',
    shoulder: 21,
    shoulderY: 25.5,
    neck: 3.4,
  },

  /** The Pale Litany. No skin at all — a skull, and the sockets are the read. */
  bone: {
    ...HUMAN,
    skin: 0xcfc6b0,
    skinShade: 0x9a9280,
    eye: 0x8fb4c6,
    glow: true,
    headW: 8.0,
    jawW: 5.0,
    top: 3.5,
    chin: 23,
    eyeY: 14,
    eyeDx: 3.5,
    eyeR: 1.9,
    ear: 'none',
    shoulder: 22,
    shoulderY: 26,
    neck: 2.6,
    extra: (k) =>
      // The jaw, hung separately with a gap of dark above it. A skull that is one
      // shape reads as an egg.
      `<path d="M${CX - k.jawW - 0.6} ${k.eyeY + 3.4} C${CX - k.jawW - 0.6} ${k.chin - 1} ${CX - 3} ${k.chin + 1.6} ${CX} ${k.chin + 1.6} ` +
      `C${CX + 3} ${k.chin + 1.6} ${CX + k.jawW + 0.6} ${k.chin - 1} ${CX + k.jawW + 0.6} ${k.eyeY + 3.4} Z" ` +
      `fill="${hex(k.skin)}"/>` +
      `<path d="M${CX - k.jawW + 0.4} ${k.eyeY + 4.6} L${CX + k.jawW - 0.4} ${k.eyeY + 4.6} L${CX + k.jawW - 0.9} ${k.eyeY + 6.4} L${CX - k.jawW + 0.9} ${k.eyeY + 6.4} Z" ` +
      `fill="${hex(0x17150f)}"/>` +
      `<path d="M${CX - 3.4} ${k.eyeY + 4.6} v1.8 M${CX - 1.2} ${k.eyeY + 4.6} v1.8 M${CX + 1.2} ${k.eyeY + 4.6} v1.8 M${CX + 3.4} ${k.eyeY + 4.6} v1.8" ` +
      `stroke="${hex(k.skin)}" stroke-width="0.75"/>` +
      // Nasal cavity: the one mark that makes a skull unmistakably a skull.
      `<path d="M${CX} ${k.eyeY + 1} L${CX + 1.5} ${k.eyeY + 3.8} L${CX - 1.5} ${k.eyeY + 3.8} Z" fill="${hex(0x17150f)}"/>`,
  },

  /** The Moonbound. A wolf's head on a man's shoulders: ears up, muzzle forward. */
  beast: {
    ...HUMAN,
    skin: 0x6d6152,
    skinShade: 0x4b4239,
    eye: 0xc2a95a,
    glow: true,
    headW: 7.6,
    jawW: 5.0,
    top: 4,
    chin: 22,
    eyeY: 13.6,
    eyeDx: 3.4,
    eyeR: 1.15,
    muzzle: 5.2,
    ear: 'beast',
    shoulder: 24,
    shoulderY: 25,
    neck: 5.0,
    extra: (k, _f, seed) =>
      // A fur ruff at the neck, as five wedges. Five reads; fifty is grey.
      `<path d="M${CX - 12} ${H} C${CX - 11} ${k.chin + 2} ${CX - 6} ${k.chin} ${CX} ${k.chin + 1} ` +
      `C${CX + 6} ${k.chin} ${CX + 11} ${k.chin + 2} ${CX + 12} ${H} Z" fill="${hex(0x584c3e)}"/>` +
      `<path d="M${CX - 10} ${H} L${CX - 7.5} ${k.chin + 3} L${CX - 5} ${H} L${CX - 2.5} ${k.chin + 2} L${CX} ${H} ` +
      `L${CX + 2.5} ${k.chin + 2} L${CX + 5} ${H} L${CX + 7.5} ${k.chin + 3} L${CX + 10} ${H} Z" ` +
      `fill="${hex(0x6e6050)}" opacity="${(0.4 + seed * 0.2).toFixed(2)}"/>`,
  },

  /** The Ironfang. Wider at the jaw than at the temple, and the tusks break the outline upward. */
  orc: {
    ...HUMAN,
    skin: 0x76806a,
    skinShade: 0x515a49,
    eye: 0xc8863a,
    glow: true,
    headW: 8.4,
    jawW: 8.6,
    top: 5,
    chin: 23,
    eyeY: 13.4,
    eyeDx: 3.8,
    eyeR: 1.05,
    ear: 'none',
    shoulder: 27,
    shoulderY: 25,
    neck: 6.4,
    extra: (k) =>
      // Tusks, up from the lower jaw and past the lip. Two white triangles are
      // the single cheapest "this is not a man" in the whole file.
      `<path d="M${CX - 4.2} ${k.chin - 1.5} L${CX - 5.6} ${k.eyeY + 3.2} L${CX - 2.9} ${k.chin - 1.2} Z" fill="${hex(0xe6ddc6)}"/>` +
      `<path d="M${CX + 4.2} ${k.chin - 1.5} L${CX + 5.6} ${k.eyeY + 3.2} L${CX + 2.9} ${k.chin - 1.2} Z" fill="${hex(0xe6ddc6)}"/>` +
      // The brow ridge, one hard bar. It is what makes the eyes read as sunk.
      `<path d="M${CX - k.headW - 0.4} ${k.eyeY - 2.4} C${CX - 4} ${k.eyeY - 4.6} ${CX + 4} ${k.eyeY - 4.6} ${CX + k.headW + 0.4} ${k.eyeY - 2.4} ` +
      `L${CX + k.headW} ${k.eyeY - 0.4} C${CX + 4} ${k.eyeY - 2.4} ${CX - 4} ${k.eyeY - 2.4} ${CX - k.headW} ${k.eyeY - 0.4} Z" ` +
      `fill="${hex(k.skinShade)}"/>`,
  },

  /** The Gutter Congregation. Mostly snout and ears, and small enough that the card is never full. */
  rat: {
    ...HUMAN,
    skin: 0x7c7060,
    skinShade: 0x554b3f,
    eye: 0xb8a13c,
    glow: true,
    headW: 6.4,
    jawW: 3.6,
    top: 7,
    chin: 21,
    eyeY: 14.4,
    eyeDx: 3.0,
    eyeR: 0.95,
    muzzle: 6.4,
    ear: 'round',
    shoulder: 19,
    shoulderY: 27,
    neck: 3.2,
    extra: (k) =>
      `<path d="M${CX + k.muzzle + 2.2} ${k.eyeY + 3.6} l1.6 -0.9 l0.2 1.7 Z" fill="${hex(0xd8c9b4)}"/>` +
      // Whiskers. Three lines, and they are the difference between a rat and a dog.
      `<path d="M${CX + k.muzzle} ${k.eyeY + 3} l6 -2.2 M${CX + k.muzzle} ${k.eyeY + 4} l6.4 0.4 M${CX + k.muzzle - 0.5} ${k.eyeY + 4.8} l5.6 2.6" ` +
      `stroke="${hex(0xa89a86)}" stroke-width="0.4" opacity="0.65"/>`,
  },

  /** The Rotmoot. Swollen, sagging, and half of it wrapped up because the rest is worse. */
  rot: {
    ...HUMAN,
    skin: 0x8a8b63,
    skinShade: 0x5f6042,
    eye: 0xcbd07a,
    glow: true,
    headW: 9.4,
    jawW: 8.6,
    top: 5,
    chin: 24,
    eyeY: 14.8,
    eyeDx: 3.4,
    eyeR: 1.35,
    ear: 'none',
    shoulder: 26,
    shoulderY: 26,
    neck: 7.0,
    extra: (k) =>
      // Sackcloth over one eye, and a jaw that has come away on that side.
      `<path d="M${CX - k.headW - 1} ${k.eyeY - 4} L${CX + 1} ${k.eyeY - 5} L${CX + 1.6} ${k.eyeY + 2.6} L${CX - k.headW - 0.6} ${k.eyeY + 3.4} Z" ` +
      `fill="${hex(0x958d74)}"/>` +
      `<path d="M${CX - k.headW - 1} ${k.eyeY - 1} L${CX + 1.3} ${k.eyeY - 2}" stroke="${hex(0x6d654f)}" stroke-width="0.6"/>` +
      `<path d="M${CX + 2} ${k.chin - 3} C${CX + 5} ${k.chin + 1} ${CX + 7} ${k.chin} ${CX + 8} ${k.chin - 3}" ` +
      `stroke="${hex(0x3d3a24)}" stroke-width="1.1" fill="none"/>`,
  },

  /** The Drowned Court. Narrow, drowned-pale, and veiled — you do not see their faces. */
  abyss: {
    ...HUMAN,
    skin: 0x9fb0b2,
    skinShade: 0x6d7d80,
    eye: 0x63b8cc,
    glow: true,
    headW: 7.2,
    jawW: 4.6,
    top: 3,
    chin: 23.5,
    eyeY: 14,
    eyeDx: 3.1,
    eyeR: 1.15,
    ear: 'fin',
    shoulder: 22,
    shoulderY: 25.5,
    neck: 3.6,
    extra: (k) =>
      // Gill slits down the neck, and a veil of weed off the jaw.
      `<path d="M${CX - k.jawW - 1} ${k.chin + 1} l-1.6 3.4 M${CX - k.jawW + 0.6} ${k.chin + 1.6} l-1.4 3.4 ` +
      `M${CX + k.jawW + 1} ${k.chin + 1} l1.6 3.4 M${CX + k.jawW - 0.6} ${k.chin + 1.6} l1.4 3.4" ` +
      `stroke="${hex(0x3f5157)}" stroke-width="0.7" opacity="0.8"/>`,
  },
};

/** Which people a faction is made of. Everything else about a card follows from this. */
const KIN_OF: Record<string, string> = {
  ashen: 'human',
  vigil: 'human',
  karrun: 'dwarf',
  verdant: 'elf',
  litany: 'bone',
  moonbound: 'beast',
  ironfang: 'orc',
  gutter: 'rat',
  rotmoot: 'rot',
  drowned: 'abyss',
  common: 'human',
};

/** The handful of units whose body is not their faction's. An ogre is not an orc. */
const KIN_OVERRIDE: Record<string, string> = {
  litany_charnelox: 'rot',
  summon_skeleton: 'bone',
  litany_wretch: 'bone',
  rot_shambler: 'rot',
};

/* ── Parts ───────────────────────────────────────────────────────────────── */

/** Head, jaw, muzzle. One closed path so the silhouette is a silhouette. */
function head(k: Kin): string {
  const m = k.muzzle;
  if (m > 0) {
    // A muzzle is drawn as a second lobe off the front of the skull rather than
    // by stretching it: stretched, a wolf's head is a sausage.
    return (
      `<path d="M${CX} ${k.top} C${CX + k.headW} ${k.top} ${CX + k.headW + 0.6} ${k.eyeY - 2} ${CX + k.headW} ${k.eyeY + 2} ` +
      `L${CX + m + 3} ${k.eyeY + 1.4} C${CX + m + 4.6} ${k.eyeY + 2.4} ${CX + m + 4.6} ${k.eyeY + 5} ${CX + m + 2.6} ${k.eyeY + 5.6} ` +
      `L${CX + k.jawW} ${k.chin - 1} C${CX + k.jawW} ${k.chin + 1.6} ${CX - k.jawW} ${k.chin + 1.6} ${CX - k.jawW} ${k.chin - 2} ` +
      `C${CX - k.headW} ${k.eyeY + 4} ${CX - k.headW - 0.6} ${k.eyeY - 2} ${CX - k.headW} ${k.top + 1.4} ` +
      `C${CX - k.headW * 0.5} ${k.top} ${CX - k.headW * 0.3} ${k.top} ${CX} ${k.top} Z" fill="${hex(k.skin)}"/>` +
      `<path d="M${CX} ${k.top} C${CX - k.headW * 0.3} ${k.top} ${CX - k.headW * 0.5} ${k.top} ${CX - k.headW} ${k.top + 1.4} ` +
      `C${CX - k.headW - 0.6} ${k.eyeY - 2} ${CX - k.headW} ${k.eyeY + 4} ${CX - k.jawW} ${k.chin - 2} ` +
      `C${CX - 2} ${k.chin} ${CX - 2.4} ${k.eyeY} ${CX - 1} ${k.top + 1} Z" fill="${hex(k.skinShade)}" opacity="0.55"/>`
    );
  }
  return (
    `<path d="M${CX} ${k.top} C${CX + k.headW * 0.75} ${k.top} ${CX + k.headW} ${k.top + 2.5} ${CX + k.headW} ${k.eyeY} ` +
    `C${CX + k.headW} ${k.eyeY + 4} ${CX + k.jawW + 1} ${k.chin - 5} ${CX + k.jawW} ${k.chin - 2} ` +
    `C${CX + k.jawW} ${k.chin + 1.4} ${CX - k.jawW} ${k.chin + 1.4} ${CX - k.jawW} ${k.chin - 2} ` +
    `C${CX - k.jawW - 1} ${k.chin - 5} ${CX - k.headW} ${k.eyeY + 4} ${CX - k.headW} ${k.eyeY} ` +
    `C${CX - k.headW} ${k.top + 2.5} ${CX - k.headW * 0.75} ${k.top} ${CX} ${k.top} Z" fill="${hex(k.skin)}"/>` +
    // The shadow half. Light comes from the upper right on every card in the
    // game, and this is the only thing giving a flat shape a direction.
    `<path d="M${CX} ${k.top} C${CX - k.headW * 0.75} ${k.top} ${CX - k.headW} ${k.top + 2.5} ${CX - k.headW} ${k.eyeY} ` +
    `C${CX - k.headW} ${k.eyeY + 4} ${CX - k.jawW - 1} ${k.chin - 5} ${CX - k.jawW} ${k.chin - 2} ` +
    `C${CX - 2} ${k.chin + 0.6} ${CX - 2.6} ${k.eyeY} ${CX - 1.4} ${k.top + 0.6} Z" ` +
    `fill="${hex(k.skinShade)}" opacity="0.55"/>`
  );
}

function ears(k: Kin): string {
  const y = k.eyeY;
  switch (k.ear) {
    case 'long':
      // Swept back and up, past the top of the skull. On an elf this is the
      // whole identification and it must break the outline, not sit inside it.
      return (
        `<path d="M${CX + k.headW - 1} ${y - 1} L${CX + k.headW + 7} ${k.top - 3.5} L${CX + k.headW + 1.5} ${y + 3} Z" fill="${hex(k.skin)}"/>` +
        `<path d="M${CX - k.headW + 1} ${y - 1} L${CX - k.headW - 7} ${k.top - 3.5} L${CX - k.headW - 1.5} ${y + 3} Z" fill="${hex(k.skinShade)}"/>`
      );
    case 'beast':
      // Up, not out. Two triangles above the skull line.
      return (
        `<path d="M${CX + 2} ${k.top + 2} L${CX + 8.5} ${k.top - 7} L${CX + 9.5} ${k.top + 3} Z" fill="${hex(k.skin)}"/>` +
        `<path d="M${CX + 3.4} ${k.top + 2} L${CX + 8} ${k.top - 3.5} L${CX + 8.6} ${k.top + 2.4} Z" fill="${hex(0x2a251e)}"/>` +
        `<path d="M${CX - 2} ${k.top + 2} L${CX - 8.5} ${k.top - 7} L${CX - 9.5} ${k.top + 3} Z" fill="${hex(k.skinShade)}"/>` +
        `<path d="M${CX - 3.4} ${k.top + 2} L${CX - 8} ${k.top - 3.5} L${CX - 8.6} ${k.top + 2.4} Z" fill="${hex(0x211d17)}"/>`
      );
    case 'round':
      return (
        `<circle cx="${CX + 4}" cy="${k.top - 0.5}" r="4.4" fill="${hex(k.skin)}"/>` +
        `<circle cx="${CX + 4}" cy="${k.top - 0.5}" r="2.4" fill="${hex(0x4a3a34)}"/>` +
        `<circle cx="${CX - 4.5}" cy="${k.top - 1}" r="4.4" fill="${hex(k.skinShade)}"/>` +
        `<circle cx="${CX - 4.5}" cy="${k.top - 1}" r="2.4" fill="${hex(0x3b2e29)}"/>`
      );
    case 'fin':
      return (
        `<path d="M${CX + k.headW - 1} ${y} L${CX + k.headW + 6} ${y - 4} L${CX + k.headW + 5.4} ${y + 1} L${CX + k.headW + 7} ${y + 3} L${CX + k.headW - 0.5} ${y + 3.5} Z" ` +
        `fill="${hex(k.skinShade)}"/>` +
        `<path d="M${CX - k.headW + 1} ${y} L${CX - k.headW - 6} ${y - 4} L${CX - k.headW - 5.4} ${y + 1} L${CX - k.headW - 7} ${y + 3} L${CX - k.headW + 0.5} ${y + 3.5} Z" ` +
        `fill="${hex(shade(k.skinShade, 0.8))}"/>`
      );
    case 'human':
      return (
        `<ellipse cx="${CX + k.headW - 0.2}" cy="${y + 1.4}" rx="1.5" ry="2.4" fill="${hex(k.skin)}"/>` +
        `<ellipse cx="${CX - k.headW + 0.2}" cy="${y + 1.4}" rx="1.5" ry="2.4" fill="${hex(k.skinShade)}"/>`
      );
    default:
      return '';
  }
}

/** The peoples that grow hair. Everyone else is bald, scaled, bandaged or bone. */
const HAIRED = new Set(['human', 'dwarf', 'elf']);

/**
 * Hair, for the heads that are not inside a closed helm.
 *
 * On the first sheet every bare human head in the game was bald — all of them,
 * because a head is a skin-coloured shape and nothing had ever been drawn on top
 * of it. Sixty identical shaved men is not a stylistic choice, it is an omission,
 * and it is the largest single reason two units of the same faction read as the
 * same unit.
 *
 * Cut and colour come from the unit id's hash. Not a roll: a Crownwood Archer
 * has the same crop in this frame, the next frame, the reload and the other
 * machine. Four cuts and four colours is sixteen heads, which across a roster of
 * this size is enough that no two neighbours in the company bar match.
 */
function hair(k: Kin, seed: number): string {
  const style = Math.floor(seed * 4) % 4;
  const cols = [0x2b241d, 0x50412c, 0x8b7d64, 0x6a5238];
  const col = cols[Math.floor(seed * 977) % 4] ?? cols[0]!;
  const lit = mix(col, 0xd8cbb0, 0.28);
  // Receding hairlines sit back off the temple; everything else overhangs it.
  const w = k.headW + (style === 3 ? -0.6 : 0.9);
  const top = k.top - (style === 3 ? 0.2 : 1.4);
  const fringe = k.eyeY - (style === 3 ? 4.2 : 2.2);

  let out =
    `<path d="M${CX - w} ${fringe} C${CX - w - 0.6} ${top} ${CX + w + 0.6} ${top} ${CX + w} ${fringe} ` +
    `C${CX + w - 1.2} ${fringe - 2.4} ${CX - w + 1.2} ${fringe - 2.4} ${CX - w} ${fringe} Z" fill="${hex(col)}"/>` +
    `<path d="M${CX + 0.5} ${top - 0.9} C${CX + w * 0.7} ${top - 0.4} ${CX + w + 0.6} ${fringe - 3} ${CX + w} ${fringe} ` +
    `C${CX + w - 1.2} ${fringe - 2.4} ${CX + 3} ${fringe - 3.2} ${CX + 0.5} ${top - 0.9} Z" fill="${hex(lit)}"/>`;

  if (style === 1) {
    // Falls past the jaw on both sides. Read as width, which is the point: a
    // long-haired silhouette is a different silhouette.
    const fall = (s: number, fill: number): string =>
      `<path d="M${CX + s * (w - 0.6)} ${fringe - 1} C${CX + s * (w + 2.6)} ${k.eyeY + 3} ${CX + s * (w + 1.8)} ${k.chin} ${CX + s * (w + 2.4)} ${k.chin + 5} ` +
      `L${CX + s * (w - 3.4)} ${k.chin + 5} C${CX + s * (w - 2.6)} ${k.chin - 2} ${CX + s * (w - 2)} ${k.eyeY + 2} ${CX + s * (w - 2.6)} ${fringe} Z" fill="${hex(fill)}"/>`;
    out += fall(-1, shade(col, 0.78)) + fall(1, col);
  } else if (style === 2) {
    // Scraped back and tied. The knot breaks the skull line behind the ear.
    out +=
      `<path d="M${CX - w + 1} ${k.eyeY - 4} C${CX - w - 4.6} ${k.eyeY - 5.6} ${CX - w - 6.4} ${k.eyeY - 1} ${CX - w - 3} ${k.eyeY + 1.6} ` +
      `C${CX - w - 5} ${k.eyeY - 2.6} ${CX - w - 2.6} ${k.eyeY - 4.6} ${CX - w + 1.4} ${k.eyeY - 2} Z" fill="${hex(shade(col, 0.82))}"/>`;
  }
  return out;
}

/**
 * Eyes, at card size.
 *
 * There is no room for an iris and a lash line here — at 88 pixels the whole eye
 * is three pixels across. So it is a slit or a socket with a lit dot in it, and
 * `glow` decides which: the things that are not alive get a light behind the
 * face, which at a glance across a battle line is worth more than any amount of
 * anatomy.
 */
function eyes(k: Kin, dark: boolean): string {
  const x = k.muzzle > 0 ? CX + 1 : CX;
  const y = k.eyeY;
  if (k.glow || dark) {
    const c = dark && !k.glow ? 0xd8c07a : k.eye;
    return (
      `<ellipse cx="${x - k.eyeDx}" cy="${y}" rx="${k.eyeR * 1.5}" ry="${k.eyeR * 1.2}" fill="${hex(c)}" opacity="0.35"/>` +
      `<ellipse cx="${x + k.eyeDx}" cy="${y}" rx="${k.eyeR * 1.5}" ry="${k.eyeR * 1.2}" fill="${hex(c)}" opacity="0.35"/>` +
      `<ellipse cx="${x - k.eyeDx}" cy="${y}" rx="${k.eyeR * 0.72}" ry="${k.eyeR * 0.62}" fill="${hex(mix(c, 0xffffff, 0.5))}"/>` +
      `<ellipse cx="${x + k.eyeDx}" cy="${y}" rx="${k.eyeR * 0.72}" ry="${k.eyeR * 0.62}" fill="${hex(mix(c, 0xffffff, 0.5))}"/>`
    );
  }
  return (
    `<path d="M${x - k.eyeDx - k.eyeR} ${y} q${k.eyeR} ${-k.eyeR * 1.2} ${k.eyeR * 2} 0 q${-k.eyeR} ${k.eyeR} ${-k.eyeR * 2} 0 Z" fill="${hex(0xf0e8d8)}"/>` +
    `<path d="M${x + k.eyeDx - k.eyeR} ${y} q${k.eyeR} ${-k.eyeR * 1.2} ${k.eyeR * 2} 0 q${-k.eyeR} ${k.eyeR} ${-k.eyeR * 2} 0 Z" fill="${hex(0xf0e8d8)}"/>` +
    `<circle cx="${x - k.eyeDx + 0.2}" cy="${y - 0.15}" r="${k.eyeR * 0.62}" fill="${hex(k.eye)}"/>` +
    `<circle cx="${x + k.eyeDx + 0.2}" cy="${y - 0.15}" r="${k.eyeR * 0.62}" fill="${hex(k.eye)}"/>` +
    // Brows. One stroke each, and without them every face on every card is
    // placid — which for an army is the wrong single expression to pick.
    `<path d="M${x - k.eyeDx - k.eyeR - 0.8} ${y - k.eyeR - 1.3} l${k.eyeR * 2.4} 0.9" stroke="${hex(k.skinShade)}" stroke-width="0.9" stroke-linecap="round"/>` +
    `<path d="M${x + k.eyeDx + k.eyeR + 0.8} ${y - k.eyeR - 1.3} l${-k.eyeR * 2.4} 0.9" stroke="${hex(k.skinShade)}" stroke-width="0.9" stroke-linecap="round"/>`
  );
}

/**
 * The helm, and whether it leaves a face to draw.
 *
 * Returns the shape and a flag: a closed helm means the head under it is drawn
 * as darkness with two lit points in it, which is both cheaper and a great deal
 * more menacing than a face behind a grille that nobody can resolve anyway.
 */
function helm(n: number, k: Kin, metal: number, accent: number): { art: string; closed: boolean } {
  const lit = mix(metal, 0xffffff, 0.32);
  const dark = shade(metal, 0.55);
  const skullTop = k.top - 1;
  switch (n) {
    case 0:
      return { art: '', closed: false };
    case 1:
      // A cap. Sits on the skull, covers nothing.
      return {
        art:
          `<path d="M${CX - k.headW - 0.6} ${k.eyeY - 3} C${CX - k.headW - 0.6} ${skullTop - 1} ${CX + k.headW + 0.6} ${skullTop - 1} ${CX + k.headW + 0.6} ${k.eyeY - 3} ` +
          `C${CX + 4} ${k.eyeY - 4.6} ${CX - 4} ${k.eyeY - 4.6} ${CX - k.headW - 0.6} ${k.eyeY - 3} Z" fill="${hex(metal)}"/>` +
          `<path d="M${CX - 1} ${skullTop - 0.6} C${CX + 5} ${skullTop} ${CX + k.headW} ${k.eyeY - 5} ${CX + k.headW + 0.6} ${k.eyeY - 3} ` +
          `C${CX + 5} ${k.eyeY - 4.4} ${CX + 2} ${k.eyeY - 4} ${CX - 1} ${skullTop - 0.6} Z" fill="${hex(lit)}"/>`,
        closed: false,
      };
    case 2:
      // Nasal helm: a brim and a bar down the nose. The bar is the read.
      return {
        art:
          `<path d="M${CX - k.headW - 1.4} ${k.eyeY - 2.4} C${CX - k.headW - 1.4} ${skullTop - 2} ${CX + k.headW + 1.4} ${skullTop - 2} ${CX + k.headW + 1.4} ${k.eyeY - 2.4} Z" fill="${hex(metal)}"/>` +
          `<path d="M${CX - k.headW - 2.6} ${k.eyeY - 2.6} L${CX + k.headW + 2.6} ${k.eyeY - 2.6} L${CX + k.headW + 2} ${k.eyeY - 0.6} L${CX - k.headW - 2} ${k.eyeY - 0.6} Z" fill="${hex(dark)}"/>` +
          `<path d="M${CX - 1.1} ${k.eyeY - 2.4} L${CX + 1.1} ${k.eyeY - 2.4} L${CX + 0.9} ${k.eyeY + 4.4} L${CX - 0.9} ${k.eyeY + 4.4} Z" fill="${hex(metal)}"/>` +
          `<path d="M${CX} ${skullTop - 1.6} C${CX + 5} ${skullTop - 1} ${CX + k.headW} ${k.eyeY - 4.4} ${CX + k.headW + 1.4} ${k.eyeY - 2.4} ` +
          `C${CX + 5} ${k.eyeY - 3.4} ${CX + 2} ${k.eyeY - 3} ${CX} ${skullTop - 1.6} Z" fill="${hex(lit)}"/>`,
        closed: false,
      };
    case 3:
      // Closed: a bucket with a cross slit. No face under it.
      return {
        art:
          `<path d="M${CX - k.headW - 1.4} ${k.eyeY - 3} C${CX - k.headW - 1.4} ${skullTop - 2.6} ${CX + k.headW + 1.4} ${skullTop - 2.6} ${CX + k.headW + 1.4} ${k.eyeY - 3} ` +
          `L${CX + k.headW + 0.6} ${k.chin - 1} C${CX + k.jawW} ${k.chin + 2.4} ${CX - k.jawW} ${k.chin + 2.4} ${CX - k.headW - 0.6} ${k.chin - 1} Z" fill="${hex(metal)}"/>` +
          `<path d="M${CX} ${skullTop - 2.2} C${CX + 5} ${skullTop - 1.6} ${CX + k.headW} ${k.eyeY - 5} ${CX + k.headW + 1.4} ${k.eyeY - 3} ` +
          `L${CX + k.headW + 0.6} ${k.chin - 1} C${CX + 4} ${k.chin + 1.6} ${CX + 2} ${k.chin + 1.8} ${CX + 1.6} ${k.chin + 1.8} ` +
          `C${CX + 3} ${k.chin - 4} ${CX + 2.4} ${k.eyeY} ${CX} ${skullTop - 2.2} Z" fill="${hex(lit)}" opacity="0.55"/>` +
          `<path d="M${CX - k.headW + 0.4} ${k.eyeY - 0.6} L${CX + k.headW - 0.4} ${k.eyeY - 0.6} L${CX + k.headW - 0.4} ${k.eyeY + 1.4} L${CX - k.headW + 0.4} ${k.eyeY + 1.4} Z" fill="${hex(0x0d0b09)}"/>` +
          `<path d="M${CX - 0.9} ${k.eyeY - 0.6} L${CX + 0.9} ${k.eyeY - 0.6} L${CX + 0.9} ${k.chin - 1} L${CX - 0.9} ${k.chin - 1} Z" fill="${hex(0x0d0b09)}"/>`,
        closed: true,
      };
    case 4:
      // Closed and horned. Reserved for the things you are meant to be afraid of.
      return {
        art:
          `<path d="M${CX - k.headW - 2} ${k.eyeY - 2} C${CX - k.headW - 2} ${skullTop - 3.4} ${CX + k.headW + 2} ${skullTop - 3.4} ${CX + k.headW + 2} ${k.eyeY - 2} ` +
          `L${CX + k.headW + 1} ${k.chin} C${CX + k.jawW} ${k.chin + 3} ${CX - k.jawW} ${k.chin + 3} ${CX - k.headW - 1} ${k.chin} Z" fill="${hex(metal)}"/>` +
          `<path d="M${CX} ${skullTop - 3} C${CX + 5} ${skullTop - 2.4} ${CX + k.headW + 1} ${k.eyeY - 4.4} ${CX + k.headW + 2} ${k.eyeY - 2} ` +
          `L${CX + k.headW + 1} ${k.chin} C${CX + 4} ${k.chin + 2.4} ${CX + 2} ${k.chin + 2.6} ${CX + 1.6} ${k.chin + 2.6} ` +
          `C${CX + 3} ${k.chin - 4} ${CX + 2.4} ${k.eyeY} ${CX} ${skullTop - 3} Z" fill="${hex(lit)}" opacity="0.5"/>` +
          `<path d="M${CX - k.headW - 1.6} ${k.eyeY - 4} C${CX - k.headW - 8} ${k.eyeY - 8} ${CX - k.headW - 8} ${skullTop - 6} ${CX - k.headW - 3} ${skullTop - 8} ` +
          `C${CX - k.headW - 5.4} ${skullTop - 4} ${CX - k.headW - 4} ${k.eyeY - 6} ${CX - k.headW + 0.4} ${k.eyeY - 2} Z" fill="${hex(0xd9cfb6)}"/>` +
          `<path d="M${CX + k.headW + 1.6} ${k.eyeY - 4} C${CX + k.headW + 8} ${k.eyeY - 8} ${CX + k.headW + 8} ${skullTop - 6} ${CX + k.headW + 3} ${skullTop - 8} ` +
          `C${CX + k.headW + 5.4} ${skullTop - 4} ${CX + k.headW + 4} ${k.eyeY - 6} ${CX + k.headW - 0.4} ${k.eyeY - 2} Z" fill="${hex(0xe8dfc6)}"/>` +
          `<path d="M${CX - k.headW + 0.6} ${k.eyeY - 0.4} L${CX - 1.4} ${k.eyeY - 0.4} L${CX - 1.4} ${k.eyeY + 1.8} L${CX - k.headW + 0.6} ${k.eyeY + 1.8} Z" fill="${hex(0x0d0b09)}"/>` +
          `<path d="M${CX + k.headW - 0.6} ${k.eyeY - 0.4} L${CX + 1.4} ${k.eyeY - 0.4} L${CX + 1.4} ${k.eyeY + 1.8} L${CX + k.headW - 0.6} ${k.eyeY + 1.8} Z" fill="${hex(0x0d0b09)}"/>`,
        closed: true,
      };
    default:
      // A cowl. The face inside is in shadow but present — a caster is a person,
      // and the point of the hood is that you can *nearly* see who.
      return {
        art:
          `<path d="M${CX - k.headW - 4.5} ${k.chin + 4} C${CX - k.headW - 5.5} ${skullTop - 3} ${CX + k.headW + 5.5} ${skullTop - 3} ${CX + k.headW + 4.5} ${k.chin + 4} ` +
          `C${CX + k.headW} ${k.chin - 1} ${CX + k.headW + 0.5} ${k.eyeY - 3} ${CX + k.headW - 0.5} ${k.eyeY - 4} ` +
          `C${CX + 4} ${k.eyeY - 7} ${CX - 4} ${k.eyeY - 7} ${CX - k.headW + 0.5} ${k.eyeY - 4} ` +
          `C${CX - k.headW - 0.5} ${k.eyeY - 3} ${CX - k.headW} ${k.chin - 1} ${CX - k.headW - 4.5} ${k.chin + 4} Z" fill="${hex(accent)}" opacity="0"/>`,
        closed: false,
      };
  }
}

/**
 * Shoulders, and this is the shape the card lives or dies on.
 *
 * It occupies the bottom two fifths of every card — more area than the head —
 * and the first version drew it as a single smooth dome from the neck out to the
 * frame edge. At card size that reads as a boulder with a face on top of it, and
 * at four times card size it reads as a boulder with a face on top of it, which
 * is how I found out. Three things fix it and all three are structure, not
 * colour:
 *
 *   the outline    a trapezius slope down to a *corner* at the deltoid, then a
 *                  straight drop. A body has a shoulder point; a dome does not
 *   the centre     lit on the near side and cut down the middle, so the two
 *                  halves separate instead of reading as one mass
 *   the collar     a V of the garment under the throat. One mark, and it is the
 *                  difference between a torso and a hill
 */
function shoulders(k: Kin, cloth: number, metal: number, pauldron: number, accent: number): string {
  const y = k.shoulderY;
  const sw = k.shoulder;
  /** Where the trapezius meets the neck. */
  const nx = k.neck + 2.2;
  const lit = mix(cloth, 0xffffff, 0.19);
  const deep = shade(cloth, 0.58);

  /** The far half and the near half, so the light has an edge to break on. */
  const half = (s: number): string =>
    `M${CX} ${y - 3.2} L${CX + s * nx} ${y - 3.2} ` +
    `C${CX + s * (sw - 7)} ${y - 2.2} ${CX + s * (sw - 1)} ${y + 0.6} ${CX + s * sw} ${y + 5} ` +
    `L${CX + s * sw} ${H} L${CX} ${H} Z`;

  let out =
    `<path d="${half(-1)}" fill="${hex(cloth)}"/>` +
    `<path d="${half(1)}" fill="${hex(lit)}"/>` +
    // Core shadow inside the far shoulder — the body turns away from the key.
    `<path d="M${CX - sw} ${H} L${CX - sw} ${y + 5} C${CX - sw + 1} ${y + 0.6} ${CX - sw + 7} ${y - 2.2} ${CX - nx} ${y - 3.2} ` +
    `L${CX - nx + 2.6} ${y - 0.6} C${CX - sw + 8} ${y + 1.6} ${CX - sw + 4.6} ${y + 4} ${CX - sw + 5} ${H} Z" ` +
    `fill="${hex(deep)}" opacity="0.8"/>` +
    // The collar. Drawn as an open V rather than a filled one so the tunic under
    // it still shows: a solid triangle here reads as a bib.
    `<path d="M${CX - nx - 1.4} ${y - 3.2} L${CX} ${y + 6} L${CX + nx + 1.4} ${y - 3.2} ` +
    `L${CX + nx - 1.2} ${y - 3.2} L${CX} ${y + 2.8} L${CX - nx + 1.2} ${y - 3.2} Z" ` +
    `fill="${hex(deep)}"/>`;

  if (pauldron >= 1) {
    // A cap over the joint, hugging the outer edge rather than floating beside it.
    const cap = (s: number, fill: number): string =>
      `<path d="M${CX + s * (sw + 1.2)} ${y + 10} C${CX + s * (sw + 1.6)} ${y + 1} ${CX + s * (sw - 2.5)} ${y - 4.6} ${CX + s * (sw - 10)} ${y - 3.6} ` +
      `C${CX + s * (sw - 8.4)} ${y + 1} ${CX + s * (sw - 7)} ${y + 5.4} ${CX + s * (sw - 6.6)} ${y + 10.5} Z" fill="${hex(fill)}"/>`;
    out += cap(-1, shade(metal, 0.5)) + cap(1, mix(metal, 0xffffff, 0.2));
  }
  if (pauldron >= 2) {
    // A second, larger plate over the first, and one line of the faction's
    // colour along the near one — the only saturation allowed on the body.
    const plate = (s: number, fill: number): string =>
      `<path d="M${CX + s * (sw + 3.4)} ${y + 11} C${CX + s * (sw + 3.6)} ${y + 0.6} ${CX + s * (sw - 1)} ${y - 6.4} ${CX + s * (sw - 11.5)} ${y - 5} ` +
      `L${CX + s * (sw - 9.6)} ${y + 0.8} C${CX + s * (sw - 2)} ${y - 0.4} ${CX + s * (sw - 1.4)} ${y + 4.6} ${CX + s * (sw - 1.8)} ${y + 11.4} Z" fill="${hex(fill)}"/>`;
    out +=
      plate(-1, shade(metal, 0.42)) +
      plate(1, mix(metal, 0xffffff, 0.3)) +
      `<path d="M${CX + sw - 11.5} ${y - 5} L${CX + sw - 9.6} ${y + 0.8} L${CX + sw - 6} ${y + 0.2} L${CX + sw - 8} ${y - 5.4} Z" fill="${hex(accent)}"/>`;
  }
  return out;
}

/**
 * The weapon, and it is doing more work than the face.
 *
 * Drawn to the right, tall, and hard against the frame edge, because at 88
 * pixels the outline of a bow and the outline of a hammer are two things a
 * player can tell apart in a quarter of a second and two faces are not. Every
 * one of these is sized to run most of the card's height for that reason.
 */
function weapon(kind: VisWeapon, metal: number, wood: number, accent: number): string {
  const lit = mix(metal, 0xffffff, 0.35);
  const x = 55;
  switch (kind) {
    case 'sword':
      return (
        `<path d="M${x - 1.4} 26 L${x - 1.4} 6 L${x} 2.5 L${x + 1.4} 6 L${x + 1.4} 26 Z" fill="${hex(metal)}"/>` +
        `<path d="M${x} 2.5 L${x + 1.4} 6 L${x + 1.4} 26 L${x} 26 Z" fill="${hex(lit)}"/>` +
        `<path d="M${x - 5.5} 26 L${x + 5.5} 26 L${x + 5.5} 28.4 L${x - 5.5} 28.4 Z" fill="${hex(shade(metal, 0.7))}"/>` +
        `<path d="M${x - 1} 28.4 L${x + 1} 28.4 L${x + 1} 35 L${x - 1} 35 Z" fill="${hex(wood)}"/>` +
        `<circle cx="${x}" cy="36.4" r="1.7" fill="${hex(accent)}"/>`
      );
    case 'spear':
      return (
        `<path d="M${x} 1 L${x + 2.6} 7 L${x} 12 L${x - 2.6} 7 Z" fill="${hex(metal)}"/>` +
        `<path d="M${x} 1 L${x + 2.6} 7 L${x} 12 Z" fill="${hex(lit)}"/>` +
        `<path d="M${x - 1} 10 L${x + 1} 10 L${x + 1} ${H} L${x - 1} ${H} Z" fill="${hex(wood)}"/>` +
        `<path d="M${x - 3.4} 13 L${x + 3.4} 13 L${x + 2.4} 15.4 L${x - 2.4} 15.4 Z" fill="${hex(accent)}"/>`
      );
    case 'axe':
      return (
        `<path d="M${x - 1.1} 6 L${x + 1.1} 6 L${x + 1.1} ${H} L${x - 1.1} ${H} Z" fill="${hex(wood)}"/>` +
        // A crescent, and it must be wide: an axe read as a hammer for two passes
        // because the head was drawn as a block.
        `<path d="M${x + 1} 5 C${x + 11} 6 ${x + 12} 17 ${x + 2} 20 L${x + 2} 16.5 ` +
        `C${x + 8} 14.5 ${x + 7.5} 9 ${x + 1} 8.5 Z" fill="${hex(metal)}"/>` +
        `<path d="M${x + 1} 5 C${x + 11} 6 ${x + 12} 17 ${x + 2} 20 C${x + 9} 15 ${x + 9} 9 ${x + 1} 7 Z" fill="${hex(lit)}"/>` +
        `<path d="M${x - 1} 5 C${x - 8} 6 ${x - 9} 14 ${x - 2} 17 L${x - 2} 14 C${x - 6} 12 ${x - 6} 9 ${x - 1} 8 Z" fill="${hex(shade(metal, 0.72))}"/>`
      );
    case 'bow':
      return (
        // The limb runs the whole height and the string is a straight line
        // against it — the contrast between the two is the whole shape.
        `<path d="M${x + 3} 2 C${x - 6} 12 ${x - 6} 28 ${x + 3} 38 L${x + 5.4} 37 ` +
        `C${x - 3} 27.5 ${x - 3} 12.5 ${x + 5.4} 3 Z" fill="${hex(wood)}"/>` +
        `<path d="M${x + 4} 2.4 L${x + 4} 37.6" stroke="${hex(0xd6cbb2)}" stroke-width="0.6"/>` +
        `<path d="M${x + 4} 20 L${x - 4} 20" stroke="${hex(0xb9ad94)}" stroke-width="0.7"/>` +
        `<path d="M${x - 4} 18.8 L${x - 7.5} 20 L${x - 4} 21.2 Z" fill="${hex(accent)}"/>`
      );
    case 'crossbow':
      return (
        `<path d="M${x - 2} 12 L${x + 2} 12 L${x + 2} 34 L${x - 2} 34 Z" fill="${hex(wood)}"/>` +
        `<path d="M${x - 9} 10 C${x - 5} 15 ${x + 5} 15 ${x + 9} 10 L${x + 9} 13 ` +
        `C${x + 5} 18 ${x - 5} 18 ${x - 9} 13 Z" fill="${hex(metal)}"/>` +
        `<path d="M${x - 9} 11.5 L${x + 9} 11.5" stroke="${hex(0xd6cbb2)}" stroke-width="0.6"/>` +
        `<path d="M${x - 0.7} 6 L${x + 0.7} 6 L${x + 0.7} 16 L${x - 0.7} 16 Z" fill="${hex(0x8f8674)}"/>` +
        `<path d="M${x} 4 L${x + 1.8} 7 L${x - 1.8} 7 Z" fill="${hex(accent)}"/>`
      );
    case 'gun':
      return (
        `<path d="M${x - 1.3} 4 L${x + 1.3} 4 L${x + 1.3} 26 L${x - 1.3} 26 Z" fill="${hex(0x4a453c)}"/>` +
        `<path d="M${x + 0.2} 4 L${x + 1.3} 4 L${x + 1.3} 26 L${x + 0.2} 26 Z" fill="${hex(0x6e685c)}"/>` +
        `<path d="M${x - 2} 26 L${x + 2.6} 26 L${x + 5} 36 L${x + 1} 36 Z" fill="${hex(wood)}"/>` +
        // The forked rest. A handgonne without one is a stick.
        `<path d="M${x - 8} 10 L${x - 5.5} 13 L${x - 5.5} 36 L${x - 7} 36 L${x - 7} 13.5 ` +
        `L${x - 9.5} 10.5 Z" fill="${hex(0x6a5a44)}"/>` +
        `<circle cx="${x}" cy="3" r="1.8" fill="${hex(accent)}" opacity="0.75"/>`
      );
    case 'staff':
      return (
        `<path d="M${x - 1} 10 L${x + 1} 10 L${x + 1} ${H} L${x - 1} ${H} Z" fill="${hex(wood)}"/>` +
        `<path d="M${x - 4.5} 8 C${x - 4.5} 2 ${x + 4.5} 2 ${x + 4.5} 8 C${x + 4.5} 12 ${x} 13.5 ${x} 13.5 ` +
        `C${x} 13.5 ${x - 4.5} 12 ${x - 4.5} 8 Z" fill="${hex(shade(metal, 0.8))}"/>` +
        `<circle cx="${x}" cy="7.6" r="3.4" fill="${hex(accent)}"/>` +
        `<circle cx="${x}" cy="7.6" r="5.4" fill="${hex(accent)}" opacity="0.28"/>` +
        `<circle cx="${x - 1}" cy="6.4" r="1.1" fill="#fff" opacity="0.8"/>`
      );
    case 'hammer':
      // Lopsided on purpose: a block one side, a spike the other, and the haft
      // running past the head. Drawn symmetrically — which it was — the outline
      // is a rectangle on a pole, and half the heavy infantry in the game were
      // carrying what read as a signpost.
      return (
        `<path d="M${x - 1.2} 3 L${x + 1.2} 3 L${x + 1.2} ${H} L${x - 1.2} ${H} Z" fill="${hex(wood)}"/>` +
        `<path d="M${x + 0.6} 7 L${x + 8.4} 8.6 L${x + 8.4} 15.4 L${x + 0.6} 17 Z" fill="${hex(metal)}"/>` +
        `<path d="M${x + 5.6} 8.2 L${x + 8.4} 8.6 L${x + 8.4} 15.4 L${x + 5.6} 15.8 Z" fill="${hex(lit)}"/>` +
        `<path d="M${x - 0.6} 9 L${x - 8} 11 L${x - 8} 13 L${x - 0.6} 15 Z" fill="${hex(shade(metal, 0.72))}"/>` +
        `<path d="M${x - 2.2} 6.6 L${x + 2.2} 6.6 L${x + 2.2} 17.4 L${x - 2.2} 17.4 Z" fill="${hex(shade(metal, 0.88))}"/>`
      );
    case 'scythe':
      return (
        `<path d="M${x - 1.1} 8 L${x + 1.1} 8 L${x + 1.1} ${H} L${x - 1.1} ${H} Z" fill="${hex(wood)}"/>` +
        `<path d="M${x} 9 C${x - 12} 8 ${x - 15} 1 ${x - 9} -1 C${x - 12} 3 ${x - 8} 6 ${x} 6.4 Z" fill="${hex(metal)}"/>` +
        `<path d="M${x} 9 C${x - 12} 8 ${x - 15} 1 ${x - 9} -1 C${x - 11} 2.4 ${x - 7} 4.6 ${x} 5 Z" fill="${hex(lit)}"/>`
      );
    case 'claw':
      // No haft: three talons off the shoulder, curved forward.
      return (
        `<path d="M${x - 4} 22 C${x + 4} 18 ${x + 8} 22 ${x + 7} 30 L${x + 4.6} 29 C${x + 5} 23.5 ${x + 2} 21.5 ${x - 3.4} 24 Z" fill="${hex(0xded2b8)}"/>` +
        `<path d="M${x - 3} 26 C${x + 5} 22 ${x + 9} 27 ${x + 8} 35 L${x + 5.6} 34 C${x + 6} 28.5 ${x + 3} 26 ${x - 2.4} 28 Z" fill="${hex(0xcfc2a6)}"/>` +
        `<path d="M${x - 2} 30 C${x + 5} 27 ${x + 9} 32 ${x + 8} ${H} L${x + 5.6} ${H} C${x + 6} 34 ${x + 3} 31.5 ${x - 1.4} 32 Z" fill="${hex(0xbfb193)}"/>`
      );
    default:
      return '';
  }
}

/** A shield, on the near side, with the faction's mark on it. */
function shieldArt(cloth: number, metal: number, accent: number): string {
  const x = 13;
  return (
    `<path d="M${x - 8} 18 L${x + 8} 18 C${x + 8} 32 ${x + 4} 38 ${x} ${H} C${x - 4} 38 ${x - 8} 32 ${x - 8} 18 Z" ` +
    `fill="${hex(shade(cloth, 0.85))}"/>` +
    `<path d="M${x} 18 L${x + 8} 18 C${x + 8} 32 ${x + 4} 38 ${x} ${H} Z" fill="${hex(mix(cloth, 0xffffff, 0.14))}"/>` +
    `<path d="M${x - 8} 18 L${x + 8} 18 L${x + 8} 20 L${x - 8} 20 Z" fill="${hex(metal)}"/>` +
    `<path d="M${x} 21 L${x + 4.5} 26 L${x} 34 L${x - 4.5} 26 Z" fill="${hex(accent)}"/>` +
    `<circle cx="${x}" cy="26" r="2" fill="${hex(metal)}"/>`
  );
}

/** A cloak, behind the shoulders. Read as width, never as detail. */
function cloakArt(cloth: number, k: Kin): string {
  return (
    `<path d="M${CX - k.shoulder - 6} ${H} C${CX - k.shoulder - 5} ${k.shoulderY} ${CX - k.neck - 4} ${k.shoulderY - 3} ${CX} ${k.shoulderY - 3} ` +
    `C${CX + k.neck + 4} ${k.shoulderY - 3} ${CX + k.shoulder + 5} ${k.shoulderY} ${CX + k.shoulder + 6} ${H} Z" ` +
    `fill="${hex(shade(cloth, 0.6))}"/>`
  );
}

/** A pennant on a pole, behind the far shoulder. The one place a card may shout. */
function bannerArt(accent: number, accentDark: number): string {
  const x = 12;
  return (
    `<path d="M${x - 0.9} 0 L${x + 0.9} 0 L${x + 0.9} ${H} L${x - 0.9} ${H} Z" fill="${hex(0x5b4c38)}"/>` +
    `<path d="M${x + 0.9} 2 L${x + 13} 4 L${x + 9.5} 9 L${x + 13} 14 L${x + 0.9} 16 Z" fill="${hex(accent)}"/>` +
    `<path d="M${x + 0.9} 2 L${x + 13} 4 L${x + 9.5} 9 L${x + 0.9} 9 Z" fill="${hex(mix(accent, 0xffffff, 0.22))}"/>` +
    `<path d="M${x + 0.9} 16 L${x + 13} 14 L${x + 12} 15.6 L${x + 0.9} 17.6 Z" fill="${hex(accentDark)}"/>`
  );
}

/* ── Builds: how the frame is used ───────────────────────────────────────── */

/** A war engine, which is not a person and should not be drawn as one. */
function engine(cloth: number, metal: number, wood: number, accent: number, heavy: boolean): string {
  const lit = mix(metal, 0xffffff, 0.3);
  return (
    // The frame first, so the wheels sit in front of it.
    `<path d="M14 24 L58 24 L56 31 L16 31 Z" fill="${hex(wood)}"/>` +
    `<path d="M14 24 L58 24 L57 27 L15 27 Z" fill="${hex(mix(wood, 0xffffff, 0.2))}"/>` +
    (heavy
      ? // A ram: the head is the thing, and it is metal and enormous.
        `<path d="M8 12 L44 14 L44 24 L8 22 Z" fill="${hex(wood)}"/>` +
        `<path d="M8 12 L44 14 L44 17 L8 15 Z" fill="${hex(mix(wood, 0xffffff, 0.18))}"/>` +
        `<path d="M8 11 L2 13 L1 21 L8 23 Z" fill="${hex(metal)}"/>` +
        `<path d="M8 11 L2 13 L2 17 L8 16 Z" fill="${hex(lit)}"/>` +
        `<path d="M18 12.4 L21 12.6 L21 23 L18 22.8 Z" fill="${hex(shade(metal, 0.8))}"/>` +
        `<path d="M30 13 L33 13.2 L33 23.6 L30 23.4 Z" fill="${hex(shade(metal, 0.8))}"/>`
      : // A shooting engine: an arm, a cord, and a bolt in the trough.
        `<path d="M20 24 L26 4 L30 5 L25 24 Z" fill="${hex(wood)}"/>` +
        `<path d="M44 24 L38 6 L42 5 L48 24 Z" fill="${hex(wood)}"/>` +
        `<path d="M27 5.5 L40 6.5" stroke="${hex(0xd6cbb2)}" stroke-width="0.9"/>` +
        `<path d="M20 14 L52 15.6 L52 18 L20 16.4 Z" fill="${hex(0x54483a)}"/>` +
        `<path d="M22 15 L56 16.8 L52.4 13.6 L56 16.8 L52 20 Z" fill="${hex(metal)}"/>` +
        `<path d="M52 13.4 L60 17 L52 20.6 Z" fill="${hex(accent)}"/>`) +
    `<circle cx="20" cy="32" r="7.4" fill="${hex(shade(wood, 0.75))}"/>` +
    `<circle cx="20" cy="32" r="7.4" fill="none" stroke="${hex(metal)}" stroke-width="1.4"/>` +
    `<path d="M20 25 v14 M13 32 h14 M15 27 l10 10 M25 27 l-10 10" stroke="${hex(shade(wood, 0.55))}" stroke-width="1.1"/>` +
    `<circle cx="20" cy="32" r="2" fill="${hex(metal)}"/>` +
    `<circle cx="48" cy="34" r="5.6" fill="${hex(shade(wood, 0.68))}"/>` +
    `<circle cx="48" cy="34" r="5.6" fill="none" stroke="${hex(shade(metal, 0.8))}" stroke-width="1.2"/>` +
    `<path d="M48 29 v10 M43 34 h10" stroke="${hex(shade(wood, 0.5))}" stroke-width="1"/>` +
    `<path d="M56 22 L58 22 L58 ${H} L56 ${H} Z" fill="${hex(cloth)}"/>`
  );
}

/* ── Assembly ────────────────────────────────────────────────────────────── */

export interface CardArtOptions {
  /** Draw the thin gilt border. Off when the container already has one. */
  frame?: boolean;
  /**
   * `bust` is the whole 68×40 composition, for the company bar.
   * `head` windows in on the head alone, for a square roster chip.
   */
  crop?: 'bust' | 'head';
  /** Dim it — a company that is spent, a unit that cannot be afforded. */
  muted?: boolean;
  /** `class` for the returned `<svg>`. */
  cls?: string;
}

interface Palette {
  cloth: number;
  metal: number;
  wood: number;
  accent: number;
  accentDark: number;
  /** The niche behind the figure: the faction's colour, taken right down. */
  auraHot: number;
  auraCold: number;
}

/**
 * A unit's colours, lifted off the ground.
 *
 * The faction cloths in `factions.ts` sit between 0x27 and 0x53 — they were
 * chosen to belong on the map, where a soldier is eight pixels tall and the
 * ground is the thing he must not disappear into. On a card the ground is
 * *also* dark, and the first version of this file used those cloths raw: every
 * card came out a brown figure on a brown rectangle with a face floating in the
 * middle of it, and the only ones that read were the Gilded Vigil's, because
 * their cloth happens to be near-white.
 *
 * So the card lifts cloth a third of the way to bone. It stays desaturated —
 * the mix is toward a warm grey, not toward colour — which keeps the ash-and-
 * bone rule while buying the two stops of separation the card needs. The
 * saturation budget is spent where the rule allows it: the niche behind the
 * figure, the banner, the shield boss, and nowhere else.
 */
function paletteOf(f: FactionDef | undefined): Palette {
  const niche = (accent: number): Pick<Palette, 'auraHot' | 'auraCold'> => ({
    auraHot: mix(accent, C.ashDeep, 0.46),
    auraCold: mix(accent, C.ashDeep, 0.82),
  });
  if (!f) {
    // The common roster — villagers, the Runeguard, summons, the five heroes.
    // Deliberately colourless: they belong to whoever is holding them, so they
    // borrow the world's own gold rather than a house's colour.
    return {
      cloth: mix(0x453c33, 0xb9ad9a, 0.34),
      metal: 0x9a9084,
      wood: 0x7a6950,
      accent: C.gold,
      accentDark: C.goldDeep,
      ...niche(C.gold),
    };
  }
  return {
    cloth: mix(f.cloth, 0xb9ad9a, 0.34),
    metal: mix(f.metal, 0xffffff, 0.1),
    wood: mix(mix(f.cloth, 0x6a5943, 0.55), 0xb9ad9a, 0.28),
    accent: f.accent,
    accentDark: f.accentDark,
    ...niche(f.accent),
  };
}

/** The tier a unit sits at, as a little rise in the frame's gilding. Cheap, and readable. */
function tierMark(tier: number, accent: number): string {
  if (tier <= 1) return '';
  let d = '';
  for (let i = 0; i < tier - 1; i++) {
    const x = W - 4 - i * 3.4;
    d += `<path d="M${x} 2 L${x + 2.2} 5.4 L${x - 2.2} 5.4 Z" fill="${hex(accent)}" opacity="0.85"/>`;
  }
  return d;
}

/**
 * One unit's card art, as an SVG string ready for `innerHTML`.
 *
 * The layer order matters as much as it does in the hero portraits, and for the
 * same reason: banner behind everything, then cloak, then the body, then the
 * head — a head drawn before the shoulders sits behind them and the unit looks
 * decapitated — then the helm over the head, then the weapon in front of it all,
 * then the light over the lot.
 */
export function unitCardArt(id: string, opts: CardArtOptions = {}): string {
  let t: UnitType;
  try {
    t = unitTypeById(id);
  } catch {
    return '';
  }
  const f = t.faction === 'common' ? undefined : faction(t.faction);
  const p = paletteOf(f);
  const kinName = KIN_OVERRIDE[t.id] ?? KIN_OF[t.faction] ?? 'human';
  const base = KIN[kinName] ?? HUMAN;
  const seed = hash(t.id);
  const v = t.vis;
  const uid = `uc-${t.id}`;
  const frame = opts.frame !== false;
  const view = opts.crop === 'head' ? `${CX - 16} 0 32 32` : `0 0 ${W} ${H}`;

  // The build decides the framing before anything else is drawn.
  const big = v.build === 'brute' || v.build === 'champion';
  const swarm = v.build === 'swarm';
  const siege = v.build === 'siege';
  // 1.38 with a 1.2 shoulder on top of it came to 1.66 across the chest, which
  // pushed the shoulder line off both edges and left the head sitting on an
  // unbroken slab. A brute should crowd the frame, not become it.
  const scale = big ? 1.2 : swarm ? 0.74 : 1;

  // A big thing is drawn bigger *and lower*, so it crowds the frame the way a
  // large model crowds a rank. Scaling alone just makes a small man far away.
  const k: Kin = big
    ? {
        ...base,
        headW: base.headW * 1.16,
        jawW: base.jawW * 1.16,
        top: base.top - 1,
        chin: base.chin + 1.6,
        eyeY: base.eyeY + 0.8,
        eyeR: base.eyeR * 1.3,
        eyeDx: base.eyeDx * 1.15,
        shoulder: base.shoulder * 1.06,
        shoulderY: base.shoulderY + 1,
        neck: base.neck * 1.25,
      }
    : base;

  const hel = siege ? { art: '', closed: false } : helm(v.helm, k, p.metal, p.accent);
  const dark = hel.closed;

  let body: string;
  if (siege) {
    body = engine(p.cloth, p.metal, p.wood, p.accent, v.scale > 1.4);
  } else if (swarm) {
    // Three of them, staggered and overlapping. A swarm card that shows one
    // model is lying about what arrives.
    // Only the one in front is kitted. The two behind are heads and shoulders,
    // because kit on a figure at 60% of card scale is mud — but with the front
    // one stripped as well, the whole Gutter Congregation came out as four
    // identical cards: they are all `build: 'swarm'`, and helm, pauldron and
    // weapon were the only things telling a Tunneller from a Censer Rat.
    const one = (dx: number, dy: number, s: number, dim: number, kit: boolean): string => {
      const hh = kit ? helm(v.helm, k, p.metal, p.accent) : { art: '', closed: false };
      return (
        `<g transform="translate(${dx} ${dy}) scale(${s})">` +
        `<g transform="translate(${CX * (1 - s)} ${H * (1 - s)})">` +
        `<g style="filter:brightness(${dim})">` +
        shoulders(k, p.cloth, p.metal, kit ? v.pauldron : 0, p.accent) +
        (hh.closed
          ? `<path d="M${CX - k.headW} ${k.top} L${CX + k.headW} ${k.top} L${CX + k.jawW} ${k.chin} L${CX - k.jawW} ${k.chin} Z" fill="${hex(0x0e0c0a)}"/>` +
            eyes({ ...k, glow: true }, true)
          : head(k) + ears(k) + eyes(k, false) + (k.extra ? k.extra(k, f, seed) : '')) +
        hh.art +
        `</g></g></g>`
      );
    };
    // Spread wider and dimmed harder than the first version, where three
    // overlapping torsos merged into one mound with three heads on it. The
    // stagger is nudged by the id hash so two swarm units of the same faction do
    // not stand in exactly the same formation.
    const j = (seed - 0.5) * 4;
    body =
      one(-19 - j, 5, 0.6, 0.42, false) +
      one(18 - j, 4, 0.66, 0.55, false) +
      one(-1 + j * 0.5, 0, 0.86, 1, true);
  } else {
    body =
      (v.cloak ? cloakArt(p.cloth, k) : '') +
      shoulders(k, p.cloth, p.metal, v.pauldron, p.accent) +
      // Neck, before the head, always. Same rule as the portraits and the same
      // bug if it is broken.
      `<path d="M${CX - k.neck} ${k.chin - 3} L${CX + k.neck} ${k.chin - 3} L${CX + k.neck} ${k.shoulderY + 1} L${CX - k.neck} ${k.shoulderY + 1} Z" fill="${hex(k.skinShade)}"/>` +
      (dark
        ? // Under a closed helm: a void with two lit points in it.
          `<path d="M${CX - k.headW} ${k.top} L${CX + k.headW} ${k.top} L${CX + k.jawW} ${k.chin} L${CX - k.jawW} ${k.chin} Z" fill="${hex(0x0e0c0a)}"/>` +
          eyes({ ...k, glow: true, eye: k.glow ? k.eye : 0xd8a24a }, true)
        : head(k) +
          ears(k) +
          (HAIRED.has(kinName) ? hair(k, seed) : '') +
          eyes(k, false) +
          (k.extra ? k.extra(k, f, seed) : '')) +
      hel.art;
  }

  const held = siege ? '' : weapon(v.weapon, p.metal, p.wood, p.accent);

  const figure =
    `<g transform="translate(${CX * (1 - scale)} ${H * (1 - scale)}) scale(${scale})">${body}</g>` +
    (v.shield && !siege ? shieldArt(p.cloth, p.metal, p.accent) : '') +
    held;

  return `<svg viewBox="${view}" preserveAspectRatio="xMidYMid meet" role="img"
     aria-label="${t.name}"${opts.cls ? ` class="${opts.cls}"` : ''}
     style="display:block;width:100%;height:100%${opts.muted ? ';filter:grayscale(0.6) brightness(0.72)' : ''}">
  <defs>
    <!--
      The niche. One radial in the faction's colour, taken down to near-black at
      the corners, so it lights the figure and vignettes the frame in a single
      pass. This is the card's whole colour identity: everything on the figure is
      ash, and the *room* is Ironfang red or Drowned blue.
    -->
    <radialGradient id="${uid}-n" cx="50%" cy="28%" r="72%">
      <stop offset="0%" stop-color="${hex(p.auraHot)}"/>
      <stop offset="52%" stop-color="${hex(p.auraCold)}"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}"/>
    </radialGradient>
    <!-- Key from the upper right, matching the hero portraits and the renderer. -->
    <linearGradient id="${uid}-k" x1="0" y1="0" x2="1" y2="0.4">
      <stop offset="0%" stop-color="#000" stop-opacity="0.34"/>
      <stop offset="45%" stop-color="#000" stop-opacity="0.08"/>
      <stop offset="80%" stop-color="#000" stop-opacity="0"/>
      <stop offset="100%" stop-color="${hex(C.goldPale)}" stop-opacity="0.14"/>
    </linearGradient>
    <!--
      The sink, and it is deliberately shallow. The first version ran from 55%
      down at 0.72 and swallowed the shoulders whole — which is the bottom third
      of every card and the only place a pauldron, a cloak or a gorget can show.
      A card is not a portrait: there is no room below the figure to sink into.
    -->
    <linearGradient id="${uid}-s" x1="0" y1="0" x2="0" y2="1">
      <stop offset="78%" stop-color="${hex(C.ashDeep)}" stop-opacity="0"/>
      <stop offset="100%" stop-color="${hex(C.ashDeep)}" stop-opacity="0.5"/>
    </linearGradient>
    <!--
      Rim light. The figure is drawn a second time, flattened to one pale colour
      and offset up and right by three quarters of a unit, *behind* itself. What
      survives is a hairline along the lit edge of whatever the silhouette
      happens to be — helm, tusk, bow limb, wheel — without any of those shapes
      having to know about it. It is the cheapest separation in the file and the
      one that makes a dark unit legible on a dark card.
    -->
    <filter id="${uid}-f" x="-20%" y="-20%" width="140%" height="140%">
      <feFlood flood-color="${hex(mix(p.accent, C.goldPale, 0.62))}" result="c"/>
      <feComposite in="c" in2="SourceGraphic" operator="in"/>
    </filter>
    <clipPath id="${uid}-c"><rect x="0" y="0" width="${W}" height="${H}"/></clipPath>
  </defs>
  <g clip-path="url(#${uid}-c)">
    <rect x="0" y="0" width="${W}" height="${H}" fill="url(#${uid}-n)"/>
    ${v.banner ? bannerArt(p.accent, p.accentDark) : ''}
    <g filter="url(#${uid}-f)" transform="translate(1.1 -0.35)" opacity="0.7">${figure}</g>
    ${figure}
    <rect x="0" y="0" width="${W}" height="${H}" fill="url(#${uid}-k)"/>
    <rect x="0" y="0" width="${W}" height="${H}" fill="url(#${uid}-s)"/>
    ${tierMark(t.tier, p.accent)}
    ${(t.flags & UF.Champion) !== 0 ? `<rect x="0" y="0" width="${W}" height="${H}" fill="none" stroke="${hex(p.accent)}" stroke-width="1.6" opacity="0.7"/>` : ''}
  </g>
  ${frame ? `<rect x="0.6" y="0.6" width="${W - 1.2}" height="${H - 1.2}" fill="none" stroke="${hex(C.goldDeep)}" stroke-width="1.2" opacity="0.55"/>` : ''}
</svg>`;
}

/* ── A whole card, for the places that do not diff ───────────────────────── */

export interface CardData {
  /** The company's name, or the unit type's. */
  name: string;
  /** `9/14`, or a bare number, or nothing. */
  count?: string;
  /** 0..1. Omit for a card that is not about a company in the field. */
  health?: number;
  /** 0..1. */
  morale?: number;
  /** Faded, for something unaffordable or spent. */
  muted?: boolean;
}

/**
 * The art with a name, a tally and two bars under it.
 *
 * The company bar in `hud.ts` builds its own cards, because it diffs them per
 * frame and rebinds their handlers — it should keep doing that and call
 * `unitCardArt` for the picture only. This is for everywhere that renders a card
 * once and throws it away: the muster panel, the roster, a tooltip, the contact
 * sheet. Same art, same class names, no bookkeeping.
 */
export function unitCard(id: string, data: CardData): string {
  const art = unitCardArt(id, { frame: false, muted: data.muted });
  const hp = data.health ?? -1;
  const hpCol = hp > 0.6 ? C.hpGood : hp > 0.3 ? C.hpMid : C.hpBad;
  return (
    `<div class="ucard">` +
    `<div class="uport">${art}` +
    (data.count ? `<span class="ucount">${data.count}</span>` : '') +
    `</div>` +
    `<div class="uname">${data.name}</div>` +
    (hp >= 0 ? `<div class="ubar hp"><i style="width:${(hp * 100).toFixed(0)}%;background:${hex(hpCol)}"></i></div>` : '') +
    (data.morale !== undefined
      ? `<div class="ubar mor"><i style="width:${(data.morale * 100).toFixed(0)}%"></i></div>`
      : '') +
    `</div>`
  );
}
